import { useEffect, useState } from "react";
import { z } from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Sparkles, CheckCircle2, Bell } from "lucide-react";
import { fbqTrack } from "@/lib/metaPixel";

const STORAGE_KEY = "se_launch_notify_seen_v1";

const schema = z.object({
  name: z.string().trim().min(2, "Numele este prea scurt").max(100),
  email: z.string().trim().email("Email invalid").max(255),
});

const RafflePopup = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setOpen(true), 4000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ name, email });
    if (!parsed.success) {
      toast({
        title: "Verifică datele",
        description: parsed.error.issues[0].message,
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase
        .from("launch_notifications")
        .insert({ full_name: parsed.data.name, email: parsed.data.email });
      if (error) throw error;
      setDone(true);
      localStorage.setItem(STORAGE_KEY, "1");
      fbqTrack("Lead", { content_name: "Premium Launch Waitlist", content_category: "popup" });
      toast({ title: "Te-am adăugat pe listă! 🎉", description: "Te anunțăm imediat ce lansăm Premium." });
    } catch (err: any) {
      toast({ title: "Hopa…", description: err.message ?? "Încearcă din nou.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogContent className="sm:max-w-md overflow-hidden border-2 border-accent/40">
        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
        <div className="absolute -left-12 -bottom-12 h-40 w-40 rounded-full bg-primary/20 blur-3xl pointer-events-none" />

        <DialogHeader className="relative">
          <div className="mb-3 inline-flex w-fit items-center gap-2 rounded-full bg-accent/15 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.18em] text-accent">
            <Sparkles className="h-3 w-3" /> În curând · Premium
          </div>
          <DialogTitle className="font-display text-3xl font-bold leading-tight">
            Fii primul care află când <span className="text-accent">lansăm Premium</span>
          </DialogTitle>
          <DialogDescription className="text-base">
            Lasă-ne numele și emailul. Îți scriem imediat ce poți accesa toate resursele Self Educate pentru BAC.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="relative space-y-4 py-2 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-accent" />
            <p className="font-display text-xl font-semibold">Ești pe listă!</p>
            <p className="text-sm text-muted-foreground">Îți trimitem un email imediat ce lansăm Premium.</p>
            <Button onClick={close} variant="warm" className="w-full">Mulțumesc!</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="relative space-y-4">
            <div className="space-y-2">
              <Label htmlFor="launch-name">Nume</Label>
              <Input id="launch-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Maria Ionescu" maxLength={100} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="launch-email">Email</Label>
              <Input id="launch-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="maria@exemplu.ro" maxLength={255} required />
            </div>
            <Button type="submit" variant="warm" size="lg" className="w-full" disabled={loading}>
              <Bell className="h-4 w-4" />
              {loading ? "Te adăugăm..." : "Anunță-mă la lansare"}
            </Button>
            <button type="button" onClick={close} className="block w-full text-center text-xs text-muted-foreground underline-offset-4 hover:underline">
              Nu, mulțumesc
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RafflePopup;
